import {
  DEFAULTS,
  FIELDS,
  onSettingChange,
  overridden,
  resetSettings,
  setSetting,
  settings,
  type SettingKey,
} from "../shared/settings";
import "./panels.css";

// Models are built from the page's initial values, including URL flags.
const loadedSettings = { ...settings };

export function mountSettingsPanel(root: HTMLElement): void {
  root.classList.add("settings-content");
  const keys = Object.keys(FIELDS) as SettingKey[];
  const refreshers = new Map<SettingKey, () => void>();
  const sections = new Map<string, HTMLElement>();

  const reloadNotice = document.createElement("div");
  reloadNotice.className = "notice settings-reload";
  reloadNotice.hidden = true;
  const reload = document.createElement("button");
  reload.type = "button";
  reload.className = "button button--primary";
  reload.textContent = "Reload to apply";
  reload.addEventListener("click", () => location.reload());
  reloadNotice.append(reload);
  root.append(reloadNotice);

  for (const key of keys) {
    const field = FIELDS[key];
    let section = sections.get(field.group);
    if (!section) {
      section = document.createElement("section");
      section.className = "panel-section settings-group";
      const heading = document.createElement("h2");
      heading.className = "section-label";
      heading.textContent = field.group;
      section.append(heading);
      root.append(section);
      sections.set(field.group, section);
    }

    const row = document.createElement("div");
    row.className = "setting-row";
    row.dataset.setting = key;
    const copy = document.createElement("details");
    copy.className = "setting-copy";
    const heading = document.createElement("summary");
    heading.className = "setting-heading";
    const label = document.createElement("div");
    label.className = "setting-label";
    label.id = `setting-${key}-label`;
    label.textContent = field.label;
    const help = document.createElement("p");
    help.className = "help";
    help.id = `setting-${key}-help`;
    help.textContent = field.help;
    heading.append(label);
    copy.append(heading, help);
    row.append(copy);
    const describedBy = `${help.id}${overridden.has(key) ? ` setting-${key}-override` : ""}`;

    if (field.options) {
      row.classList.add("setting-row--options");
      const choices = document.createElement("div");
      choices.className = "segmented";
      choices.setAttribute("role", "radiogroup");
      choices.setAttribute("aria-labelledby", label.id);
      choices.setAttribute("aria-describedby", describedBy);
      const options = field.options;
      const buttons = options.map((option, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("role", "radio");
        button.textContent = option.label;
        button.addEventListener("click", () => setSetting(key, option.value));
        button.addEventListener("keydown", (event) => {
          let next = index;
          if (event.key === "ArrowRight" || event.key === "ArrowDown")
            next = (index + 1) % options.length;
          else if (event.key === "ArrowLeft" || event.key === "ArrowUp")
            next = (index + options.length - 1) % options.length;
          else if (event.key === "Home") next = 0;
          else if (event.key === "End") next = options.length - 1;
          else return;
          event.preventDefault();
          setSetting(key, options[next].value);
          buttons[next].focus();
        });
        choices.append(button);
        return button;
      });
      refreshers.set(key, () => {
        buttons.forEach((button, index) => {
          const checked = settings[key] === options[index].value;
          button.setAttribute("aria-checked", String(checked));
          button.tabIndex = checked ? 0 : -1;
        });
      });
      row.append(choices);
    } else {
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "switch";
      toggle.setAttribute("role", "switch");
      toggle.setAttribute("aria-labelledby", label.id);
      toggle.setAttribute("aria-describedby", describedBy);
      toggle.addEventListener("click", () => setSetting(key, !settings[key]));
      refreshers.set(key, () => {
        const checked = Boolean(settings[key]);
        toggle.setAttribute("aria-checked", String(checked));
      });
      row.append(toggle);
    }
    if (overridden.has(key)) {
      const override = document.createElement("p");
      override.className = "setting-override";
      override.id = `setting-${key}-override`;
      override.textContent =
        "Set by a URL flag for this load. The URL takes priority again on reload.";
      row.append(override);
    }
    section.append(row);
    refreshers.get(key)?.();
  }

  const footer = document.createElement("section");
  footer.className = "panel-section settings-defaults";
  const defaults = document.createElement("button");
  defaults.type = "button";
  defaults.className = "button button--ghost";
  defaults.textContent = "Restore defaults";
  const message = document.createElement("p");
  message.className = "help";
  message.hidden = true;
  message.setAttribute("role", "status");
  defaults.addEventListener("click", () => {
    resetSettings();
    message.textContent = "Defaults restored.";
    message.hidden = false;
  });
  footer.append(defaults, message);
  root.append(footer);

  const refresh = (): void => {
    reloadNotice.hidden = !keys.some(
      (key) => FIELDS[key].reload && settings[key] !== loadedSettings[key],
    );
    defaults.disabled = keys.every((key) => settings[key] === DEFAULTS[key]);
  };
  onSettingChange((key) => {
    refreshers.get(key)?.();
    message.textContent = "";
    message.hidden = true;
    refresh();
  });
  refresh();
}
